import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso'
import { messagesApi } from '../../api/messages'
import { chatsApi } from '../../api/chats'
import { usersApi } from '../../api/users'
import { groupsApi } from '../../api/groups'
import { userName } from '../../lib/displayName'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'
import { useUiStore } from '../../stores/uiStore'
import { useMessages } from '../../hooks/useMessages'
import { wsClient } from '../../ws/client'
import { messageCache } from '../../lib/messageCache'
import { MessageItem } from './MessageItem'
import { PinnedMessageBanner } from './PinnedMessageBanner'
import { ForwardModal } from './ForwardModal'
import { TypingIndicator } from './TypingIndicator'
import { Spinner } from '../ui/Spinner'
import type { Message } from '../../types'

interface DateDivider { type: 'date-divider'; date: string; key: string }
interface UnreadDivider { type: 'unread-divider'; key: string }
type ListItem = Message | DateDivider | UnreadDivider

function formatDate(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (d.toDateString() === today.toDateString()) return 'Сегодня'
  if (d.toDateString() === yesterday.toDateString()) return 'Вчера'
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

interface PinnedEntry {
  id: string
  message_id: string
  chat_id: string
  pinned_at: string
  pinned_by: string
  message?: { id: string; text?: string }
}

interface Props { chatId: string }

// Virtual index space: data[i] has virtual index FIRST_IDX + i at mount.
// Prepending N items decreases FIRST_IDX by N, keeping all existing virtual indices stable.
const FIRST_IDX = 100_000

export function MessageList({ chatId }: Props) {
  const { messages, loading, loadingMore, loadMore, hasMore, jumpToDate } = useMessages(chatId)
  const { userId } = useAuthStore()
  const typingSet = useChatStore((s) => s.typingUsers[chatId])
  const activeChat = useChatStore((s) => s.chats.find((c) => c.id === chatId))
  const currentUnread = useChatStore((s) => s.unread[chatId] ?? 0)
  const { removeMessage, updateMessage, resetUnread, setUnread } = useChatStore()
  const { addToast, setReplyTo, setEditMessage, highlightedMessageId } = useUiStore()

  const [forwardMessage, setForwardMessage] = useState<Message | null>(null)
  const [pinnedMessages, setPinnedMessages] = useState<PinnedEntry[]>([])
  const [atBottom, setAtBottom] = useState(true)
  const atBottomRef = useRef(true)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [jumpDate, setJumpDate] = useState('')
  const [senderNames, setSenderNames] = useState<Record<string, string>>({})
  const [senderAvatars, setSenderAvatars] = useState<Record<string, string | null>>({})
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const selectMode = selectedIds.size > 0

  const virtuosoRef = useRef<VirtuosoHandle>(null)

  // Virtual-index offset: decrements as older messages are prepended
  const [firstItemIndex, setFirstItemIndex] = useState(FIRST_IDX)

  // Per-chat state for initial scroll and unread tracking
  const prevFirstMsgIdRef = useRef<string | null>(null)
  const prevListLengthRef = useRef(0)
  const lastReadMsgIdRef = useRef<string | null>(null)
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firstUnreadDataIdxRef = useRef(-1) // data index of first unread message
  const initialUnreadRef = useRef(0)

  // Whether Virtuoso is ready to mount (initial scroll index computed)
  const [scrollReady, setScrollReady] = useState(false)
  const initialScrollIndexRef = useRef(0)
  // Frozen unread count captured once on chat open — used for unread divider position
  const [frozenUnread, setFrozenUnread] = useState(0)

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])
  const clearSelect = () => setSelectedIds(new Set())

  const resolveSenderName = useCallback(async (senderId: string) => {
    if (senderNames[senderId]) return
    try {
      const user = await usersApi.getProfile(senderId)
      setSenderNames((prev) => ({
        ...prev,
        [senderId]: [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || senderId,
      }))
      setSenderAvatars((prev) => ({ ...prev, [senderId]: user.avatar_url ?? null }))
    } catch { /* ignore */ }
  }, [senderNames])

  useEffect(() => {
    if (activeChat?.type !== 'group') return
    groupsApi.listMembers(chatId)
      .then((res) => {
        const nameMap: Record<string, string> = {}
        const avatarMap: Record<string, string | null> = {}
        ;(res.members ?? []).forEach((m) => {
          if (m.user) {
            nameMap[m.user_id] = [m.user.first_name, m.user.last_name].filter(Boolean).join(' ') || m.user.username || m.user_id
            avatarMap[m.user_id] = m.user.avatar_url ?? null
          }
        })
        setSenderNames((prev) => ({ ...prev, ...nameMap }))
        setSenderAvatars((prev) => ({ ...prev, ...avatarMap }))
      })
      .catch(() => {})
  }, [chatId, activeChat?.type]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPinnedMessages([])
    chatsApi.getPinned(chatId)
      .then((res) => {
        const r = res as { pinned?: PinnedEntry[] }
        if (r.pinned) setPinnedMessages(r.pinned)
      })
      .catch(() => {})
  }, [chatId])

  // Reset all per-chat state when chat changes
  useEffect(() => {
    setFirstItemIndex(FIRST_IDX)
    prevFirstMsgIdRef.current = null
    prevListLengthRef.current = 0
    lastReadMsgIdRef.current = null
    firstUnreadDataIdxRef.current = -1
    initialUnreadRef.current = 0
    setAtBottom(true)
    atBottomRef.current = true
    setScrollReady(false)
    setFrozenUnread(0)
    if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current)
  }, [chatId])

  const listItems = useMemo<ListItem[]>(() => {
    const items: ListItem[] = []
    let lastDate = ''
    // Insert unread divider before the first unread message (frozenUnread messages from end)
    const unreadStart = frozenUnread > 0 ? messages.length - frozenUnread : -1
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i]
      const msgDate = new Date(msg.created_at).toDateString()
      if (msgDate !== lastDate) {
        items.push({ type: 'date-divider', date: formatDate(msg.created_at), key: `divider-${msgDate}` })
        lastDate = msgDate
      }
      if (i === unreadStart) {
        items.push({ type: 'unread-divider', key: 'unread-divider' })
      }
      items.push(msg)
    }
    return items
  }, [messages, frozenUnread])

  // Compute initial scroll index once per chat (first time messages arrive).
  // scrollReady is in deps so that when the reset effect sets it to false,
  // this effect re-fires immediately (prevents spinner hang on chat switch).
  useEffect(() => {
    if (scrollReady) return
    if (!listItems.length) {
      // Empty chat or still loading — but if loading is done, show empty state
      if (!loading) setScrollReady(true)
      return
    }

    const unread = useChatStore.getState().unread[chatId] ?? activeChat?.unread_count ?? 0
    initialUnreadRef.current = unread

    if (unread <= 0) {
      // All read: start at bottom
      initialScrollIndexRef.current = listItems.length - 1
      firstUnreadDataIdxRef.current = listItems.length // no unread
      setScrollReady(true)
      return
    }

    setFrozenUnread(unread)

    // Find first unread message (count N messages from the end)
    let msgCount = 0
    for (let i = listItems.length - 1; i >= 0; i--) {
      if (!('type' in listItems[i])) {
        msgCount++
        if (msgCount >= unread) {
          initialScrollIndexRef.current = i
          firstUnreadDataIdxRef.current = i
          atBottomRef.current = false
          setAtBottom(false)
          setScrollReady(true)
          return
        }
      }
    }
    // Fewer messages loaded than unread count (old messages not in cache)
    initialScrollIndexRef.current = 0
    firstUnreadDataIdxRef.current = 0
    atBottomRef.current = false
    setAtBottom(false)
    setScrollReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems, chatId, scrollReady, loading])

  // Track prepended items: when messages[0] changes, firstItemIndex must decrease
  useEffect(() => {
    if (!messages.length) return
    const firstId = messages[0].id
    if (prevFirstMsgIdRef.current !== null && prevFirstMsgIdRef.current !== firstId) {
      const diff = listItems.length - prevListLengthRef.current
      if (diff > 0) setFirstItemIndex((prev) => prev - diff)
    }
    prevFirstMsgIdRef.current = firstId
    prevListLengthRef.current = listItems.length
  }, [messages, listItems.length])

  // Mark-read helpers
  const sendMarkRead = useCallback((msgId: string) => {
    if (lastReadMsgIdRef.current === msgId) return
    lastReadMsgIdRef.current = msgId
    wsClient.sendMarkRead(chatId, msgId)
    chatsApi.markRead(chatId, msgId).catch(() => {})
    messageCache.updateUnread(chatId, 0)
  }, [chatId])

  // When user reaches the bottom: all messages are read
  const handleAtBottomChange = useCallback((isAtBottom: boolean) => {
    setAtBottom(isAtBottom)
    atBottomRef.current = isAtBottom
    if (isAtBottom && messages.length > 0) {
      const last = messages[messages.length - 1]
      sendMarkRead(last.id)
      resetUnread(chatId)
    }
  }, [messages, chatId, resetUnread, sendMarkRead])

  // Per-visible-message read tracking: update badge as messages scroll into view
  const handleRangeChanged = useCallback(({ endIndex }: { startIndex: number; endIndex: number }) => {
    if (firstUnreadDataIdxRef.current < 0 || endIndex < firstUnreadDataIdxRef.current) return

    // Find the newest visible message (skip date dividers)
    for (let i = endIndex; i >= 0; i--) {
      const item = listItems[i]
      if (item && !('type' in item)) {
        const msg = item as Message

        // Debounce: don't send on every scroll frame
        if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current)
        markReadTimerRef.current = setTimeout(() => {
          sendMarkRead(msg.id)
        }, 400)

        // Optimistic badge update: count still-unread messages after visible area
        let unreadAfter = 0
        for (let j = endIndex + 1; j < listItems.length; j++) {
          if (!('type' in listItems[j])) unreadAfter++
        }
        const remaining = Math.min(initialUnreadRef.current, unreadAfter)
        const current = useChatStore.getState().unread[chatId] ?? 0
        if (remaining < current) setUnread(chatId, remaining)

        break
      }
    }
  }, [listItems, chatId, setUnread, sendMarkRead])

  // Re-mark read when user returns to tab
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !atBottomRef.current || !messages.length) return
      const last = messages[messages.length - 1]
      sendMarkRead(last.id)
      resetUnread(chatId)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [chatId, messages, resetUnread, sendMarkRead])

  // followOutput: auto-scroll to bottom for own messages; for others only if already at bottom
  const followOutput = useCallback((isAtBottom: boolean) => {
    const last = messages[messages.length - 1]
    if (last?.sender_id === userId) return 'auto'
    return isAtBottom ? 'auto' : false
  }, [messages, userId])

  // Scroll to search-result / highlighted message
  useEffect(() => {
    if (!highlightedMessageId || !listItems.length) return
    const dataIdx = listItems.findIndex(
      (item) => !('type' in item) && (item as Message).id === highlightedMessageId,
    )
    if (dataIdx >= 0) {
      virtuosoRef.current?.scrollToIndex({
        index: firstItemIndex + dataIdx,
        behavior: 'smooth',
        align: 'center',
      })
    }
  }, [highlightedMessageId]) // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback(() => {
    virtuosoRef.current?.scrollToIndex({
      index: firstItemIndex + listItems.length - 1,
      behavior: 'smooth',
      align: 'end',
    })
  }, [firstItemIndex, listItems.length])

  const handleDelete = async (messageId: string, forEveryone: boolean) => {
    try {
      await messagesApi.delete(chatId, messageId, forEveryone)
      if (forEveryone) {
        updateMessage(chatId, messageId, { deleted_for_all: true })
      } else {
        removeMessage(chatId, messageId)
      }
    } catch {
      addToast('error', 'Не удалось удалить сообщение')
    }
  }

  const handlePin = async (msg: Message) => {
    try {
      if (msg.is_pinned) {
        await chatsApi.unpinMessage(chatId, msg.id)
        updateMessage(chatId, msg.id, { is_pinned: false })
        setPinnedMessages((prev) => prev.filter((p) => p.message_id !== msg.id))
      } else {
        await chatsApi.pinMessage(chatId, msg.id)
        updateMessage(chatId, msg.id, { is_pinned: true })
        setPinnedMessages((prev) => [
          ...prev,
          {
            id: msg.id,
            message_id: msg.id,
            chat_id: chatId,
            pinned_at: new Date().toISOString(),
            pinned_by: userId ?? '',
            message: { id: msg.id, text: msg.text },
          },
        ])
      }
    } catch {
      addToast('error', 'Не удалось закрепить сообщение')
    }
  }

  const renderItem = useCallback((_index: number, item: ListItem) => {
    if ('type' in item && item.type === 'date-divider') {
      return (
        <div className="flex justify-center py-3">
          <span className="text-txt-meta text-[12px] px-3 py-1 bg-[#182533cc] rounded-full select-none">
            {item.date}
          </span>
        </div>
      )
    }

    if ('type' in item && item.type === 'unread-divider') {
      return (
        <div className="flex justify-center py-2 my-1">
          <span className="text-txt-secondary text-[12px] px-4 py-1 bg-[#182533cc] rounded-full select-none flex items-center gap-1.5">
            Непрочитанные сообщения
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </span>
        </div>
      )
    }

    const msg = item as Message
    const msgIdx = messages.indexOf(msg)
    const prevMsg = msgIdx > 0 ? messages[msgIdx - 1] : null
    const nextMsg = msgIdx < messages.length - 1 ? messages[msgIdx + 1] : null
    const isGrouped = !!(
      prevMsg &&
      prevMsg.sender_id === msg.sender_id &&
      new Date(msg.created_at).getTime() - new Date(prevMsg.created_at).getTime() < 2 * 60 * 1000
    )
    const isLastInCluster = !nextMsg ||
      nextMsg.sender_id !== msg.sender_id ||
      new Date(nextMsg.created_at).getTime() - new Date(msg.created_at).getTime() >= 2 * 60 * 1000
    const isHighlighted = msg.id === highlightedMessageId
    const isGroup = activeChat?.type === 'group'
    const isMine = msg.sender_id === userId

    return (
      <div
        data-message-id={msg.id}
        className={isHighlighted ? 'bg-accent/10 transition-colors duration-500 rounded-lg' : undefined}
      >
        <MessageItem
          message={msg}
          isMine={isMine}
          senderName={
            !isMine && isGroup
              ? (senderNames[msg.sender_id] ?? (() => { resolveSenderName(msg.sender_id); return msg.sender_id })())
              : !isMine
                ? (activeChat?.peer ? userName(activeChat.peer) : null)
                : null
          }
          senderAvatar={isGroup && !isMine ? (senderAvatars[msg.sender_id] ?? null) : undefined}
          showSenderAvatar={isGroup && !isMine ? isLastInCluster : undefined}
          replyMessage={msg.reply_to_message_id
            ? (messages.find((m) => m.id === msg.reply_to_message_id) ?? null)
            : null}
          isGrouped={isGrouped}
          onReply={(quotedText) => setReplyTo({ chatId, messageId: msg.id, text: quotedText ?? msg.text })}
          onEdit={() => setEditMessage({ chatId, messageId: msg.id, text: msg.text })}
          onDelete={(forEveryone) => handleDelete(msg.id, forEveryone)}
          onForward={() => { setForwardMessage(msg); clearSelect() }}
          onPin={() => handlePin(msg)}
          isSelected={selectedIds.has(msg.id)}
          selectMode={selectMode}
          onSelect={toggleSelect}
        />
      </div>
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, userId, activeChat, senderNames, senderAvatars, highlightedMessageId, chatId, selectedIds, selectMode, toggleSelect])

  const typingUserIds = Array.from(typingSet ?? []).filter((id) => id !== userId)
  typingUserIds.forEach((id) => { if (!senderNames[id]) resolveSenderName(id) })
  const typingNames = activeChat?.type === 'group'
    ? typingUserIds.map((id) => senderNames[id] ?? '...')
    : typingUserIds.map(() => '')

  const isInitialLoading = loading && !messages.length
  const showVirtuoso = scrollReady && listItems.length > 0

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {pinnedMessages.length > 0 && (
        <PinnedMessageBanner
          chatId={chatId}
          pinned={pinnedMessages}
          onUnpin={(messageId) => {
            setPinnedMessages((prev) => prev.filter((p) => p.message_id !== messageId))
            updateMessage(chatId, messageId, { is_pinned: false })
          }}
        />
      )}

      {loading && messages.length > 0 && (
        <div className="flex justify-center py-1">
          <Spinner size={14} />
        </div>
      )}

      <div className="flex-1 relative min-h-0">
        {(isInitialLoading || !showVirtuoso) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size={32} />
          </div>
        )}

        {showVirtuoso && (
          <Virtuoso
            key={chatId}
            ref={virtuosoRef}
            style={{ height: '100%' }}
            firstItemIndex={firstItemIndex}
            initialTopMostItemIndex={initialScrollIndexRef.current}
            data={listItems}
            itemContent={renderItem}
            atBottomStateChange={handleAtBottomChange}
            rangeChanged={handleRangeChanged}
            startReached={() => { if (hasMore && !loadingMore) loadMore() }}
            followOutput={followOutput}
            overscan={400}
            components={{
              Header: () => (
                <div className="flex justify-center py-3">
                  {loadingMore && <Spinner size={20} />}
                </div>
              ),
            }}
          />
        )}

        {!atBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-bg-input shadow-xl
                       flex items-center justify-center text-txt-primary
                       hover:bg-bg-hover transition-all z-10 animate-fade-in"
            title="Прокрутить вниз"
          >
            {currentUnread > 0 && (
              <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-accent text-white text-[11px] font-semibold px-1.5 min-w-[20px] h-5 rounded-full flex items-center justify-center">
                {currentUnread > 99 ? '99+' : currentUnread}
              </span>
            )}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        )}

        <div className="absolute top-3 right-3 z-10">
          {showDatePicker ? (
            <div className="bg-bg-primary border border-divider rounded-xl shadow-lg p-3 flex gap-2 items-center animate-fade-in">
              <input
                type="date"
                value={jumpDate}
                onChange={(e) => setJumpDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="bg-bg-input text-txt-primary text-sm rounded-lg px-2 py-1 outline-none focus:ring-1 focus:ring-accent"
              />
              <button
                onClick={async () => {
                  if (!jumpDate) return
                  await jumpToDate(new Date(jumpDate))
                  setShowDatePicker(false)
                }}
                className="bg-accent text-white text-xs px-3 py-1.5 rounded-lg hover:bg-accent/80 transition-colors"
              >
                Go
              </button>
              <button onClick={() => setShowDatePicker(false)} className="text-txt-meta hover:text-txt-primary text-sm">✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowDatePicker(true)}
              className="w-8 h-8 rounded-full bg-bg-primary/80 border border-divider shadow-sm
                         flex items-center justify-center text-txt-meta hover:text-txt-primary
                         hover:bg-bg-hover transition-all"
              title="Перейти к дате"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {selectMode && (
        <div className="flex items-center justify-between px-4 py-2 bg-accent/10 border-t border-divider animate-fade-in">
          <button onClick={clearSelect} className="text-txt-secondary hover:text-txt-primary text-sm transition-colors">
            ✕ Cancel
          </button>
          <span className="text-txt-primary text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                const ids = Array.from(selectedIds)
                await Promise.all(ids.map((id) => messagesApi.delete(chatId, id, false).catch(() => {})))
                ids.forEach((id) => removeMessage(chatId, id))
                clearSelect()
              }}
              className="text-red-400 hover:text-red-300 text-sm transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => {
                const firstId = Array.from(selectedIds)[0]
                const msg = messages.find((m) => m.id === firstId)
                if (msg) setForwardMessage(msg)
              }}
              className="text-accent hover:opacity-80 text-sm transition-colors"
            >
              Forward
            </button>
          </div>
        </div>
      )}

      <TypingIndicator names={typingNames} />

      {forwardMessage && (
        <ForwardModal
          message={forwardMessage}
          onClose={() => setForwardMessage(null)}
        />
      )}
    </div>
  )
}
